import React, { useCallback, useEffect, useState } from "react";

import { racehorse360 } from "@tsg/1st-grpc-web-rh360";

import AppPage from "components/AppPage";
import AppPageContent from "components/AppPageContent";
import { getDateRangeQuery } from "components/DateRangeSelect/helpers";
import Loader from "components/Loader";
import NoContent from "components/NoContent";
import { useRacehorse360Api } from "hooks/api";
import { SortOrder } from "interfaces/SortOrder";
import { TSupportedEntity } from "utils/app-search-utils";
import { allFacilitiesOption } from "utils/facilities";
import { checkVideosActiveTab, EVideoListTab } from "./helpers";
import TrackOpticsContent from "./TrackOpticsContent";
import TrackOpticsPageHeader from "./TrackOpticsPageHeader";
import TrackOpticsTabs from "./TrackOpticsTabs";
import { useTrackOpticsStore } from "./useTrackOpticsStore";

import useStyles from "./styles";

const NoContentMessage = "No Videos Found";

const TrackOpticsPage = () => {
  const classes = useStyles();

  const [videos, setVideos] = useState<racehorse360.ITrackOpticsVideo[]>([]);
  const [selectedVideo, setSelectedVideo] =
    useState<racehorse360.ITrackOpticsVideo>(videos[0]);
  const [activeTab, setActiveTab] = useState(EVideoListTab.WORKOUTS);
  const { selectedFacility, convertedToDateSelectedDateRange } =
    useTrackOpticsStore();

  const [searchValue, setSearchValue] = useState(null);
  const [shouldShowLoader, setShouldShowLoader] = useState(false);
  const [requestedVideoStatuses, setRequestedVideoStatuses] = useState([
    racehorse360.TrackOpticsVideoStatus.TRACK_OPTICS_VIDEO_STATUS_IN_PROGRESS
  ]);

  const { useListTrackOpticsVideos } = useRacehorse360Api();

  const getFacilityIds = () => {
    if (!selectedFacility || selectedFacility.id === allFacilitiesOption.id) {
      return;
    }

    return [selectedFacility.id];
  };

  const {
    isLoading: isVideosListLoading,
    isFetching: isVideosListFetching,
    isPending: isVideosListPending,
    refetch: updateVideoList
  } = useListTrackOpticsVideos(
    {
      query: {
        statuses: requestedVideoStatuses,
        horseName: searchValue ? { exact: searchValue } : null,
        date: getDateRangeQuery(convertedToDateSelectedDateRange),
        facilityIds: getFacilityIds()
      },
      getOptions: {
        select: ["videoId", "facility.name", "date", "status"],
        orderBy: [`date ${SortOrder.DESC}`]
      }
    },
    {
      enabled: Boolean(selectedFacility && convertedToDateSelectedDateRange),
      onSuccess: data => {
        if (data) {
          setVideos(data.trackOpticsVideos);
          setSelectedVideo(data.trackOpticsVideos[0]);
          setShouldShowLoader(false);

          const hasVideoWithInProgressStatus = data.trackOpticsVideos.some(
            item =>
              item.status ===
              racehorse360.TrackOpticsVideoStatus
                .TRACK_OPTICS_VIDEO_STATUS_IN_PROGRESS
          );
          const hasVideoWithReviewedStatus = data.trackOpticsVideos.some(
            item =>
              item.status ===
              racehorse360.TrackOpticsVideoStatus
                .TRACK_OPTICS_VIDEO_STATUS_REVIEWED
          );
          const { isWorkoutsTabActive, isReviewedTabActive } =
            checkVideosActiveTab(activeTab);
          const shouldSwitchToReviewedTab = [
            isWorkoutsTabActive,
            !hasVideoWithInProgressStatus,
            hasVideoWithReviewedStatus
          ].every(Boolean);
          const shouldSwitchToWorkoutsTab = [
            isReviewedTabActive,
            hasVideoWithInProgressStatus,
            !hasVideoWithReviewedStatus
          ].every(Boolean);

          if (shouldSwitchToReviewedTab) {
            setActiveTab(EVideoListTab.REVIEWED);
          }
          if (shouldSwitchToWorkoutsTab) {
            setActiveTab(EVideoListTab.WORKOUTS);
          }
        }
      }
    }
  );

  const handleClickOnSuggestedItem = useCallback(
    (entity: racehorse360.IHorse) => {
      setRequestedVideoStatuses([
        racehorse360.TrackOpticsVideoStatus
          .TRACK_OPTICS_VIDEO_STATUS_IN_PROGRESS,
        racehorse360.TrackOpticsVideoStatus.TRACK_OPTICS_VIDEO_STATUS_REVIEWED
      ]);
      setSearchValue(entity.name);
    },
    [setSearchValue]
  );

  const handleChangeSearchValue = useCallback(
    (value: string, entity?: TSupportedEntity) => {
      if (entity) {
        handleClickOnSuggestedItem(entity);
      } else {
        setSearchValue(null);
      }
    },
    [setSearchValue]
  );

  const handleChangeActiveTab = (tab: EVideoListTab) => {
    const { isWorkoutsTabActive, isReviewedTabActive } =
      checkVideosActiveTab(tab);

    if (isWorkoutsTabActive) {
      setRequestedVideoStatuses([
        racehorse360.TrackOpticsVideoStatus
          .TRACK_OPTICS_VIDEO_STATUS_IN_PROGRESS
      ]);
    } else if (isReviewedTabActive) {
      setRequestedVideoStatuses([
        racehorse360.TrackOpticsVideoStatus.TRACK_OPTICS_VIDEO_STATUS_REVIEWED
      ]);
    }

    setActiveTab(tab);
  };

  const isVideosLoading = [
    isVideosListLoading,
    isVideosListFetching,
    isVideosListPending
  ].some(Boolean);

  useEffect(() => {
    if (isVideosLoading) {
      setShouldShowLoader(true);
    }
  }, [isVideosLoading]);

  const renderContent = () => {
    if (shouldShowLoader) {
      return <Loader className={classes.loader} />;
    }

    if (!videos.length) {
      return (
        <NoContent className={classes.noContent}>{NoContentMessage}</NoContent>
      );
    }

    return (
      <TrackOpticsContent
        activeTab={activeTab}
        videos={videos}
        selectedVideo={selectedVideo}
        onChangeSelectedVideo={setSelectedVideo}
        onUpdateVideoList={updateVideoList}
      />
    );
  };

  return (
    <AppPage className={classes.pageRoot}>
      <TrackOpticsPageHeader
        onItemClick={handleClickOnSuggestedItem}
        onSearch={handleChangeSearchValue}
        clearSearch={Boolean(!searchValue)}
      />

      <AppPageContent className={classes.pageContent}>
        <TrackOpticsTabs
          activeTab={activeTab}
          onChangeActiveTab={handleChangeActiveTab}
        />

        {renderContent()}
      </AppPageContent>
    </AppPage>
  );
};

export default TrackOpticsPage;
